import React, { useState, useEffect } from "react";
import {
  TextField,
  Grid,
  Button,
  Autocomplete,
  Box,
  Snackbar,
  Alert,
  IconButton,
  Paper,
  Typography,
  Tooltip,
  Chip,
} from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import {
  branchpartyList,
  getFreightsByPartyIdApi,
  issueCreate,
} from "../../../Services/Services";
import { T } from "../../../theme";

const sq = { borderRadius: "4px !important" };

const ghostBtn = {
  ...sq,
  border: `1.5px solid ${T.border}`,
  bgcolor: T.white,
  color: T.primary,
  fontWeight: 600,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.white,
    borderColor: T.borderHover,
    boxShadow: "none",
  },
};

const primaryBtn = {
  ...sq,
  bgcolor: T.primary,
  color: T.white,
  fontWeight: 700,
  fontSize: "0.72rem",
  textTransform: "none",
  boxShadow: "none",
  "&:hover": {
    bgcolor: T.primaryDark,
    boxShadow: "0 2px 8px rgba(79,70,229,0.3)",
  },
};

const tableInputSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    ...sq,
    height: 34,
    fontSize: "0.72rem",
    bgcolor: T.white,
    "& fieldset": { border: `1.5px solid ${T.border}` },
    "&:hover fieldset": { border: `1.5px solid ${T.borderHover}` },
    "&.Mui-focused fieldset": { border: `1.5px solid ${T.primary}` },
    "&.Mui-focused": { boxShadow: `0 0 0 3px ${T.primaryLight}` },
  },
  "& .MuiInputLabel-root": { fontSize: "0.74rem" },
  "& .MuiInputLabel-root.Mui-focused": { color: T.primary },
  "& .MuiOutlinedInput-input": { py: 0.8 },
};

const autoCompleteSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    ...sq,
    minHeight: 34,
    fontSize: "0.72rem",
    bgcolor: T.white,
    "& fieldset": { border: `1.5px solid ${T.border}` },
    "&:hover fieldset": { border: `1.5px solid ${T.borderHover}` },
    "&.Mui-focused fieldset": { border: `1.5px solid ${T.primary}` },
    "&.Mui-focused": { boxShadow: `0 0 0 3px ${T.primaryLight}` },
    pr: "34px !important",
  },
  "& .MuiInputLabel-root": { fontSize: "0.74rem" },
  "& .MuiInputLabel-root.Mui-focused": { color: T.primary },
  "& .MuiAutocomplete-input": { py: "6px !important", fontSize: "0.72rem" },
};

const readOnlyInputSx = {
  width: "100%",
  "& .MuiOutlinedInput-root": {
    ...sq,
    height: 34,
    fontSize: "0.72rem",
    bgcolor: "#f3f4f6",
    "& fieldset": { border: `1.5px solid ${T.border}` },
    "&.Mui-disabled fieldset": { border: `1.5px solid ${T.border}` },
  },
  "& .MuiInputLabel-root": { fontSize: "0.74rem" },
  "& .MuiOutlinedInput-input": { py: 0.8 },
};

export default function IssueForm({
  setIssues,
  setOpenAddModal,
  setSnackbar,
  onSuccess,
}) {
  const emptyRow = {
    freight_id: "",
    branch_id: "",
    party_id: "",
    truck_no: "",
    description: "",
    recovery_amount: "",
    freightOptions: [],
  };

  const [issueRows, setIssueRows] = useState([emptyRow]);
  const [partyOptions, setParties] = useState([]);
  const [branchOptions, setBranch] = useState([]);

  useEffect(() => {
    const fetchData = async () => {
      const data = await branchpartyList();
      if (data) {
        setParties(data.party);
        setBranch(data.branch);
      }
    };
    fetchData();
  }, []);

  const handleChange = (index, name, value) => {
    const updatedRows = [...issueRows];
    updatedRows[index][name] = value;
    setIssueRows(updatedRows);
  };

  const handlePartyChange = async (index, newValue) => {
    const updatedRows = [...issueRows];
    const party_id = newValue?.party_id || "";
    updatedRows[index].party_id = party_id;
    updatedRows[index].freight_id = "";
    updatedRows[index].truck_no = "";
    updatedRows[index].freightOptions = [];
    if (party_id) {
      const freights = await getFreightsByPartyIdApi(party_id);
      updatedRows[index].freightOptions = freights || [];
    }
    setIssueRows(updatedRows);
  };

  const handleFreightChange = (index, newValue) => {
    const updatedRows = [...issueRows];
    updatedRows[index].freight_id = newValue?.freight_id || "";
    updatedRows[index].truck_no = newValue?.truck_no || "";
    setIssueRows(updatedRows);
  };

  const handleAddRow = () => {
    setIssueRows([...issueRows, { ...emptyRow }]);
  };

  const handleRemoveRow = (index) => {
    const updated = [...issueRows];
    updated.splice(index, 1);
    setIssueRows(updated);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = issueRows.map(({ freightOptions, ...rest }) => ({
        ...rest,
        recovery_amount: parseFloat(rest.recovery_amount) || 0,
      }));
      const response = await issueCreate(payload);
      setIssues(response);
      setOpenAddModal(false);
      onSuccess?.(); // ✅ refresh list after create
      setSnackbar?.({
        open: true,
        message: "Issues created successfully!",
        severity: "success",
      });
    } catch (error) {
      setSnackbar?.({
        open: true,
        message: "Failed to create issues.",
        severity: "error",
      });
    }
  };

  return (
    <>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          flex: 1,
          minHeight: 0,
          bgcolor: T.white,
          overflow: "hidden",
        }}
      >
        {/* ── Top Header ────────────────────────────────── */}
        <Box
          sx={{
            flexShrink: 0,
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            px: 2,
            py: 1.25,
            borderBottom: `1px solid ${T.border}`,
            bgcolor: T.white,
          }}
        >
          <Typography
            sx={{ fontWeight: 700, fontSize: "0.82rem", color: T.primary }}
          >
            Add Issues
          </Typography>
          <Chip
            label={`${issueRows.length} Row${issueRows.length > 1 ? "s" : ""}`}
            size="small"
            sx={{
              ...sq,
              height: 18,
              bgcolor: T.primaryBg,
              color: T.primary,
              border: `1px solid ${T.border}`,
              fontSize: "0.6rem",
              fontWeight: 700,
              "& .MuiChip-label": { px: 0.75 },
            }}
          />
        </Box>

        {/* ── Form (flex col, fills remaining height) ─── */}
        <form
          onSubmit={handleSubmit}
          style={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minHeight: 0, // ✅ critical for nested flex scroll
            overflow: "hidden",
          }}
        >
          {/* ── Scrollable Rows Area ───────────────────── */}
          <Box
            sx={{
              flex: 1,
              overflowY: "auto",
              overflowX: "hidden",
              minHeight: 0, // ✅ critical
              display: "flex",
              flexDirection: "column",
              gap: 1,
              px: 2,
              py: 1.5,
              "&::-webkit-scrollbar": { width: 5 },
              "&::-webkit-scrollbar-thumb": {
                bgcolor: "rgba(79,70,229,0.18)",
                borderRadius: 3,
              },
            }}
          >
            {issueRows.map((row, index) => (
              <Paper
                key={index}
                elevation={0}
                sx={{
                  ...sq,
                  border: `1.5px solid ${T.border}`,
                  overflow: "hidden",
                  boxShadow: "0 1px 6px rgba(79,70,229,0.05)",
                  flexShrink: 0, // ✅ prevent cards from shrinking
                }}
              >
                {/* Card Header */}
                <Box
                  sx={{
                    px: 1.25,
                    py: 0.7,
                    borderBottom: `1px solid ${T.border}`,
                    bgcolor: T.headerBg, // ✅ fixed color
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                  }}
                >
                  <Chip
                    label={`Issue #${index + 1}`}
                    size="small"
                    sx={{
                      ...sq,
                      height: 16,
                      bgcolor: T.white,
                      color: T.primary,
                      border: `1px solid ${T.border}`,
                      fontSize: "0.58rem",
                      fontWeight: 700,
                      "& .MuiChip-label": { px: 0.75 },
                    }}
                  />
                  {issueRows.length > 1 && (
                    <Tooltip title="Remove row" arrow>
                      <IconButton
                        size="small"
                        onClick={() => handleRemoveRow(index)}
                        sx={{
                          width: 20,
                          height: 20,
                          ...sq,
                          color: T.red,
                          bgcolor: T.white,
                          border: `1px solid ${T.redBorder}`,
                          "&:hover": { bgcolor: T.redBg },
                        }}
                      >
                        <DeleteIcon sx={{ fontSize: 11 }} />
                      </IconButton>
                    </Tooltip>
                  )}
                </Box>

                {/* Card Fields */}
                <Box sx={{ p: 1.25 }}>
                  <Grid container spacing={1}>
                    <Grid item xs={12} sm={6} md={3}>
                      <Autocomplete
                        options={partyOptions}
                        getOptionLabel={(option) => option?.name || ""}
                        value={
                          partyOptions.find(
                            (p) => p.party_id === row.party_id,
                          ) || null
                        }
                        onChange={(e, newValue) =>
                          handlePartyChange(index, newValue)
                        }
                        ListboxProps={{ style: { maxHeight: 220 } }}
                        sx={autoCompleteSx}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Party"
                            required
                            fullWidth
                            size="small"
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={2}>
                      <Autocomplete
                        options={row.freightOptions || []}
                        getOptionLabel={(option) =>
                          option?.lr_number ? `${option.lr_number}` : ""
                        }
                        value={
                          row.freightOptions?.find(
                            (f) => f.freight_id === row.freight_id,
                          ) || null
                        }
                        onChange={(e, newValue) =>
                          handleFreightChange(index, newValue)
                        }
                        ListboxProps={{ style: { maxHeight: 220 } }}
                        sx={autoCompleteSx}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="LR Number"
                            required
                            fullWidth
                            size="small"
                          />
                        )}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={2}>
                      <TextField
                        label="Truck No."
                        value={row.truck_no}
                        InputProps={{ readOnly: true }}
                        fullWidth
                        size="small"
                        sx={readOnlyInputSx}
                      />
                    </Grid>

                    <Grid item xs={12} sm={6} md={2}>
                      <TextField
                        label="Recovery Amount"
                        type="number"
                        value={row.recovery_amount}
                        onChange={(e) =>
                          handleChange(index, "recovery_amount", e.target.value)
                        }
                        fullWidth
                        size="small"
                        inputProps={{ min: 0, step: "0.01" }}
                        sx={tableInputSx}
                      />
                    </Grid>

                    <Grid item xs={12} sm={12} md={3}>
                      <TextField
                        label="Description"
                        value={row.description}
                        onChange={(e) =>
                          handleChange(index, "description", e.target.value)
                        }
                        fullWidth
                        required
                        size="small"
                        sx={tableInputSx}
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Paper>
            ))}
          </Box>

          {/* ✅ Footer — always at bottom, never scrolls */}
          <Box
            sx={{
              flexShrink: 0,
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              flexDirection: { xs: "column", sm: "row" },
              gap: 1,
              px: 2,
              py: 1.25,
              borderTop: `1.5px solid ${T.border}`,
              bgcolor: T.white,
              boxShadow: "0 -2px 8px rgba(0,0,0,0.04)",
            }}
          >
            <Button
              size="small"
              startIcon={<AddIcon sx={{ fontSize: 14 }} />}
              onClick={handleAddRow}
              sx={{
                ...ghostBtn,
                height: 32,
                px: 2,
                minWidth: { xs: "100%", sm: 120 },
                width: { xs: "100%", sm: "auto" },
              }}
            >
              Add Row
            </Button>

            <Button
              size="small"
              type="submit"
              sx={{
                ...primaryBtn,
                height: 32,
                px: 2.5,
                minWidth: { xs: "100%", sm: 150 },
                width: { xs: "100%", sm: "auto" },
              }}
            >
              Submit Issues
            </Button>
          </Box>
        </form>
      </Box>
    </>
  );
}
